import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import PageHeader from '../components/PageHeader'
import { PageLayout, Card, Button, ListCard, ListCardItem, Badge } from '../components/ui'
import { trainerSettings } from '../api/client'

function ThemeToggle() {
  const { isDark, toggleTheme } = useTheme()

  return (
    <button
      onClick={toggleTheme}
      className={`
        relative w-14 h-8 rounded-full transition-colors duration-300
        ${isDark ? 'bg-lume-teal' : 'bg-gray-300'}
      `}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <span
        className={`
          absolute top-1 w-6 h-6 rounded-full transition-all duration-300
          flex items-center justify-center
          ${isDark
            ? 'left-7 bg-dial-black'
            : 'left-1 bg-white shadow-md'
          }
        `}
      >
        {isDark ? (
          <svg className="w-4 h-4 text-lume-teal" fill="currentColor" viewBox="0 0 20 20">
            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
          </svg>
        )}
      </span>
    </button>
  )
}

function SmsAlertToggle({ isDark }) {
  const [smsEnabled, setSmsEnabled] = useState(true)
  const [phone, setPhone] = useState('')
  const [twilioConfigured, setTwilioConfigured] = useState(false)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  async function loadSettings() {
    try {
      const data = await trainerSettings.get()
      setSmsEnabled(data.smsAlertsEnabled)
      setPhone(data.phone || '')
      setTwilioConfigured(data.twilioConfigured)
    } catch (err) {
      console.error('Failed to load SMS settings:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleToggle() {
    const newValue = !smsEnabled
    setSmsEnabled(newValue)
    setToggling(true)
    try {
      await trainerSettings.update({ smsAlertsEnabled: newValue })
    } catch (err) {
      console.error('Failed to update SMS setting:', err)
      setSmsEnabled(!newValue) // revert
    } finally {
      setToggling(false)
    }
  }

  async function handleTestSms() {
    setTestSending(true)
    setTestResult(null)
    try {
      const result = await trainerSettings.sendTestSms()
      setTestResult(result.success ? 'sent' : 'failed')
    } catch (err) {
      setTestResult('failed')
    } finally {
      setTestSending(false)
      setTimeout(() => setTestResult(null), 5000)
    }
  }

  if (loading) return null

  const canSend = twilioConfigured && phone

  return (
    <Card>
      <h3 className={`font-semibold mb-4 font-display ${
        isDark ? 'text-indices-bright' : 'text-gray-900'
      }`}>SMS Escalation Alerts</h3>

      {/* Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className={isDark ? 'text-indices-bright' : 'text-gray-900'}>
            SMS Alerts
          </p>
          <p className={`text-sm ${isDark ? 'text-indices-muted' : 'text-gray-500'}`}>
            Get texted when AI escalates urgent issues
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={!canSend || toggling}
          className={`
            relative w-14 h-8 rounded-full transition-colors duration-300
            ${!canSend || toggling ? 'opacity-40 cursor-not-allowed' : ''}
            ${smsEnabled && canSend ? (isDark ? 'bg-lume-teal' : 'bg-green-500') : (isDark ? 'bg-dial-elevated' : 'bg-gray-300')}
          `}
        >
          <span
            className={`
              absolute top-1 w-6 h-6 rounded-full transition-all duration-300
              ${isDark ? 'bg-dial-black' : 'bg-white shadow-md'}
              ${smsEnabled && canSend ? 'left-7' : 'left-1'}
            `}
          />
        </button>
      </div>

      {/* Status */}
      <div className={`text-sm space-y-1 ${isDark ? 'text-indices-muted' : 'text-gray-500'}`}>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${
            canSend
              ? (isDark ? 'bg-lume-teal' : 'bg-green-500')
              : (isDark ? 'bg-status-error' : 'bg-red-500')
          }`} />
          <span>
            Twilio: {twilioConfigured ? 'Configured' : 'Not configured'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${
            phone
              ? (isDark ? 'bg-lume-teal' : 'bg-green-500')
              : (isDark ? 'bg-status-error' : 'bg-red-500')
          }`} />
          <span>
            Phone: {phone || 'Not set in profile'}
          </span>
        </div>
      </div>

      {/* Test Button */}
      {canSend && (
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={handleTestSms}
            disabled={testSending}
            className={`text-sm px-4 py-2 rounded-lg transition-colors ${
              isDark
                ? 'bg-dial-elevated text-indices-bright hover:bg-dial-border'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${testSending ? 'opacity-50' : ''}`}
          >
            {testSending ? 'Sending...' : 'Send Test SMS'}
          </button>
          {testResult === 'sent' && (
            <span className={`text-sm ${isDark ? 'text-lume-teal' : 'text-green-600'}`}>
              Sent!
            </span>
          )}
          {testResult === 'failed' && (
            <span className={`text-sm ${isDark ? 'text-status-error' : 'text-red-600'}`}>
              Failed
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

function ProfileField({ label, value, isDark }) {
  return (
    <div>
      <label className={`text-xs font-medium ${isDark ? 'text-indices-muted' : 'text-gray-500'}`}>
        {label}
      </label>
      <p className={`mt-0.5 ${isDark ? 'text-indices-bright' : 'text-gray-900'}`}>
        {value || <span className={isDark ? 'text-indices-recessed' : 'text-gray-400'}>Not set</span>}
      </p>
    </div>
  )
}

function EditProfileModal({ trainer, isDark, onClose, onSave }) {
  const [form, setForm] = useState({
    firstName: trainer?.firstName || '',
    lastName: trainer?.lastName || '',
    email: trainer?.email || '',
    phone: trainer?.phone || '',
    specialties: trainer?.specialties || '',
    bio: trainer?.bio || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required')
      return
    }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Valid email is required')
      return
    }

    setSaving(true)
    try {
      const result = await trainerSettings.updateProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        specialties: form.specialties.trim(),
        bio: form.bio.trim(),
      })
      onSave(result.trainer)
    } catch (err) {
      setError(err.message || 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm ${
    isDark
      ? 'bg-dial-black border-dial-border text-indices-bright placeholder-indices-recessed focus:border-lume-teal'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-primary-500'
  } outline-none transition-colors`

  const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-indices-muted' : 'text-gray-500'}`

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className={`w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl ${
          isDark ? 'bg-dial-surface' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className={`sticky top-0 flex items-center justify-between px-5 py-4 border-b ${
            isDark ? 'bg-dial-surface border-dial-border' : 'bg-white border-gray-200'
          }`}>
            <h2 className={`text-lg font-semibold font-display ${isDark ? 'text-indices-bright' : 'text-gray-900'}`}>
              Edit Profile
            </h2>
            <button type="button" onClick={onClose} className={`p-1 rounded-lg ${isDark ? 'text-indices-muted hover:text-indices-bright' : 'text-gray-400 hover:text-gray-600'}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <fieldset disabled={saving} className="px-5 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>First Name *</label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={e => handleChange('firstName', e.target.value)}
                  className={inputClass}
                  maxLength={100}
                />
              </div>
              <div>
                <label className={labelClass}>Last Name *</label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={e => handleChange('lastName', e.target.value)}
                  className={inputClass}
                  maxLength={100}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
                className={inputClass}
                placeholder="(555) 555-5555"
              />
            </div>

            <div>
              <label className={labelClass}>Specialties</label>
              <input
                type="text"
                value={form.specialties}
                onChange={e => handleChange('specialties', e.target.value)}
                className={inputClass}
                placeholder="e.g. Strength Training, Nutrition"
              />
            </div>

            <div>
              <label className={labelClass}>Bio</label>
              <textarea
                value={form.bio}
                onChange={e => handleChange('bio', e.target.value)}
                className={`${inputClass} resize-none`}
                rows={4}
                placeholder="Tell clients about yourself..."
              />
            </div>

            {error && (
              <p className={`text-sm ${isDark ? 'text-status-error' : 'text-red-600'}`}>{error}</p>
            )}
          </fieldset>

          <div className={`sticky bottom-0 px-5 py-4 border-t flex gap-3 ${
            isDark ? 'bg-dial-surface border-dial-border' : 'bg-white border-gray-200'
          }`}>
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isDark
                  ? 'bg-dial-elevated text-indices-bright hover:bg-dial-border'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isDark
                  ? 'bg-lume-teal text-dial-black hover:bg-lume-teal/90'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              } ${saving ? 'opacity-50' : ''}`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const { trainer, logout, updateTrainer } = useAuth()
  const { isDark } = useTheme()
  const [showEdit, setShowEdit] = useState(false)

  async function handleLogout() {
    await logout()
    navigate('/login')
  }

  function handleSave(updatedTrainer) {
    updateTrainer(updatedTrainer)
    setShowEdit(false)
  }

  const initials = trainer
    ? `${trainer.firstName?.[0] || ''}${trainer.lastName?.[0] || ''}`.toUpperCase()
    : 'T'

  return (
    <PageLayout>
      <PageHeader title="Settings" showBack backTo="/" />

      <div className="px-4 py-4 space-y-4">
        {/* Profile Card */}
        <Card padding="lg">
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-semibold font-display shrink-0 ${
              isDark
                ? 'bg-lume-teal/20 text-lume-teal'
                : 'bg-primary-100 text-primary-600'
            }`}>
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className={`text-xl font-semibold font-display ${
                isDark ? 'text-indices-bright' : 'text-gray-900'
              }`}>
                {trainer?.firstName} {trainer?.lastName}
              </h2>
              <p className={`truncate ${isDark ? 'text-indices-muted' : 'text-gray-500'}`}>
                {trainer?.email}
              </p>
              {trainer?.specialties && (
                <p className={`text-sm mt-1 ${isDark ? 'text-lume-teal' : 'text-primary-600'}`}>
                  {trainer.specialties}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowEdit(true)}
              className={`p-2 rounded-lg shrink-0 transition-colors ${
                isDark
                  ? 'text-lume-teal hover:bg-dial-elevated'
                  : 'text-primary-600 hover:bg-gray-100'
              }`}
              aria-label="Edit profile"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
        </Card>

        {/* Profile Details */}
        <Card>
          <h3 className={`font-semibold mb-3 font-display ${
            isDark ? 'text-indices-bright' : 'text-gray-900'
          }`}>Profile Details</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ProfileField label="First Name" value={trainer?.firstName} isDark={isDark} />
              <ProfileField label="Last Name" value={trainer?.lastName} isDark={isDark} />
            </div>
            <ProfileField label="Email" value={trainer?.email} isDark={isDark} />
            <ProfileField label="Phone" value={trainer?.phone} isDark={isDark} />
            <ProfileField label="Specialties" value={trainer?.specialties} isDark={isDark} />
            {trainer?.bio && (
              <div>
                <label className={`text-xs font-medium ${isDark ? 'text-indices-muted' : 'text-gray-500'}`}>
                  Bio
                </label>
                <p className={`mt-0.5 text-sm leading-relaxed ${isDark ? 'text-indices-medium' : 'text-gray-600'}`}>
                  {trainer.bio}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Appearance Settings */}
        <Card>
          <h3 className={`font-semibold mb-4 font-display ${
            isDark ? 'text-indices-bright' : 'text-gray-900'
          }`}>Appearance</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className={isDark ? 'text-indices-bright' : 'text-gray-900'}>
                Dark Mode
              </p>
              <p className={`text-sm ${isDark ? 'text-indices-muted' : 'text-gray-500'}`}>
                {isDark ? 'Panerai dark theme' : 'Light theme'}
              </p>
            </div>
            <ThemeToggle />
          </div>
        </Card>

        {/* SMS Alert Settings */}
        <SmsAlertToggle isDark={isDark} />

        {/* Quick Stats */}
        <ListCard>
          <ListCardItem>
            <div className="flex items-center justify-between w-full">
              <span className={isDark ? 'text-indices-medium' : 'text-gray-600'}>
                Account Status
              </span>
              <Badge variant="success">Active</Badge>
            </div>
          </ListCardItem>
          <ListCardItem>
            <div className="flex items-center justify-between w-full">
              <span className={isDark ? 'text-indices-medium' : 'text-gray-600'}>
                Version
              </span>
              <span className={isDark ? 'text-indices-bright' : 'text-gray-900'}>
                Forge Trainer 1.0
              </span>
            </div>
          </ListCardItem>
        </ListCard>

        {/* Actions */}
        <ListCard>
          <ListCardItem
            onClick={() => window.location.href = '/wa/default'}
            showChevron
          >
            <span className={isDark ? 'text-indices-bright' : 'text-gray-900'}>
              Switch to Classic View
            </span>
          </ListCardItem>
        </ListCard>

        {/* Logout */}
        <Button
          variant="danger"
          fullWidth
          onClick={handleLogout}
        >
          Sign Out
        </Button>

        {/* Footer */}
        <div className={`text-center text-xs pt-4 ${
          isDark ? 'text-indices-recessed' : 'text-gray-400'
        }`}>
          <p>Forge Personal Training</p>
          <p className="mt-1">
            Powered by <span className={isDark ? 'text-lume-teal' : 'text-primary-600'}>Claude AI</span>
          </p>
        </div>
      </div>

      {showEdit && (
        <EditProfileModal
          trainer={trainer}
          isDark={isDark}
          onClose={() => setShowEdit(false)}
          onSave={handleSave}
        />
      )}
    </PageLayout>
  )
}
